import { useSpace } from '@/store/modules/App/space'
import { defineStore } from 'pinia'
import { client } from '@/services/client'
import type { AxiosResponse } from 'axios'

import type {
  Datasource,
  Datasources,
  DatasourceResponse,
  DatasourceEntryResponse,
  DatasourceEntriesResponse,
} from '@/types'

export type State = {
  datasources: Datasource[] | undefined
  total: number
}

export const useDatasources = defineStore('datasource', {
  state: (): State => ({
    datasources: undefined,
    total: 0,
  }),
  actions: {
    setDatasources(response: AxiosResponse): void {
      this.datasources = response.data.datasources
      this.total = parseInt(response.headers.total)
    },

    async loadDatasource({
      datasourceId,
      spaceId,
    }: {
      datasourceId: string
      spaceId: string
    }): Promise<Datasource> {
      return await client.get(`spaces/${spaceId}/datasources/${datasourceId}`, {
        region: useSpace().region,
      })
    },
    async loadDatasources({
      params,
      spaceId,
    }: {
      params: object
      spaceId: string
    }): Promise<Datasources> {
      const response = await client.get(`spaces/${spaceId}/datasources`, {
        params,
        region: useSpace().region,
      })

      this.setDatasources(response)
      return response.data
    },
    async addDatasource({
      payload,
      spaceId,
    }: {
      payload: object
      spaceId: string
    }): Promise<DatasourceResponse> {
      return await client.post(`spaces/${spaceId}/datasources`, payload, {
        region: useSpace().region,
      })
    },
    async addDatasourceEntry({
      payload,
      spaceId,
    }: {
      payload: object
      spaceId: string
    }): Promise<DatasourceEntryResponse> {
      return await client.post(
        `spaces/${spaceId}/datasource_entries`,
        payload,
        {
          region: useSpace().region,
        },
      )
    },
    async updateDatasourceEntry({
      payload,
      spaceId,
      entryId,
    }: {
      payload: object
      spaceId: string
      entryId: string
    }): Promise<DatasourceEntryResponse> {
      return await client.put(
        `spaces/${spaceId}/datasource_entries/${entryId}`,
        payload,
        {
          region: useSpace().region,
        },
      )
    },
    async updateDatasourceEntryPosition({
      payload,
      spaceId,
      entryId,
    }: {
      payload: object
      spaceId: string
      entryId: string
    }): Promise<DatasourceEntryResponse> {
      return await client.put(
        `spaces/${spaceId}/datasource_entries/${entryId}`,
        payload,
        {
          region: useSpace().region,
        },
      )
    },
    async deleteDatasource({
      spaceId,
      datasourceId,
    }: {
      spaceId: string
      datasourceId: string
    }): Promise<void> {
      return await client.delete(
        `spaces/${spaceId}/datasources/${datasourceId}`,
        {
          region: useSpace().region,
        },
      )
    },
    async updateDatasource({
      spaceId,
      datasourceId,
      payload,
    }: {
      spaceId: string
      datasourceId: string
      payload: object
    }): Promise<DatasourceResponse> {
      return await client.put(
        `spaces/${spaceId}/datasources/${datasourceId}`,
        payload,
        {
          region: useSpace().region,
        },
      )
    },

    async deleteDatasourceEntry({
      spaceId,
      entryId,
    }: {
      spaceId: string
      entryId: string
    }): Promise<void> {
      return await client.delete(
        `spaces/${spaceId}/datasource_entries/${entryId}`,
        {
          region: useSpace().region,
        },
      )
    },

    async loadDatasourceEntries({
      spaceId,
      clientParams,
    }: {
      spaceId: string
      clientParams: object
    }): Promise<AxiosResponse<DatasourceEntriesResponse>> {
      return client.get(`spaces/${spaceId}/datasource_entries`, {
        params: clientParams,
        region: useSpace().region,
      })
    },

    async importDatasourceEntries(payload: {
      fileData: string | ArrayBuffer | null
      dimensionId: string
      datasourceId: string
      spaceId: string
    }): Promise<unknown> {
      const { spaceId, datasourceId, fileData, dimensionId } = payload

      const payloadData: {
        csv: string | ArrayBuffer | null
        datasource_id: string
        dimension?: string
      } = {
        csv: fileData,
        datasource_id: datasourceId,
      }

      if (dimensionId) {
        payloadData.dimension = dimensionId
      }

      return await client.post(
        `/spaces/${spaceId}/datasource_entries/import`,
        payloadData,
        {
          region: useSpace().region,
        },
      )
    },
  },
})

export type DatasourcesStore = ReturnType<typeof useDatasources>
